import { CSSProperties } from 'react';
import { DataRequestFormatService } from '@vivli/features/data-requests/infrastructure/service';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useActiveUserContext, useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { useResearchTeamMembers } from '@vivli/features/data-requests/infrastructure/hook';
import { TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { useTranslation } from 'react-i18next';
import { AdminHeaderDetailTitleView, AdminHeaderTitleView } from '@vivli/shared/views';

const titleContainerStyle: CSSProperties = {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 7,
    marginBottom: 7,
    marginLeft: 30,
    flex: 1,
    minHeight: 35
};

interface RequestTitleComponentProps {
    myDataRequests?: boolean;
    // DEV-691: the live form value, threaded down from RequestHeaderContainerComponent's
    // useWatch. The header has to track what the user is typing without the form's dirty
    // state being disturbed, so the title is read from the form rather than synced back
    // into the data request on blur.
    title?: string;
}

export const RequestTitleComponent = ({ myDataRequests, title }: RequestTitleComponentProps) => {
    const { user: activeUser } = useActiveUserContext();
    const { dataRequest } = useDataRequestContext();
    const displayTitle = title ?? dataRequest.requestTitle;
    const { isAmr } = useAssignedAppTypeContext();
    const { getLeadResearcher } = useResearchTeamMembers();
    // researchTeamDetails already contains the lead/statistician, so a single membership check covers them.
    const isResearchTeamMember = dataRequest.researchTeam?.researchTeamDetails.some((rtd) => rtd.userId === activeUser.userId);
    const lead = getLeadResearcher(dataRequest.researchTeam);
    const piName = `${lead?.firstName} ${lead?.lastName}`;
    const lrName = dataRequest.antimicrobialDetail?.amrResearcherDetails?.researcherName
        ? `${dataRequest.antimicrobialDetail?.amrResearcherDetails?.researcherName}`
        : '';
    const pi = isAmr ? lrName : piName;
    const { t } = useTranslation();
    const duaTranslation = t(TranslationKeyEnum.duaValidatedStatusString);
    const statusMessage = DataRequestFormatService.mapStatusToMessage(DataRequestStatusEnum[dataRequest.status], isAmr, duaTranslation);
    const adminDetailedHeader = (
        <AdminHeaderDetailTitleView
            requestNumber={dataRequest.dataRequestDoi ? parseInt(dataRequest.dataRequestDoi, 10) : 0}
            title={dataRequest.requestTitle}
            status={statusMessage}
        />
    );

    return (
        <div style={titleContainerStyle} title={displayTitle}>
            {myDataRequests ? (
                <AdminHeaderDetailTitleView
                    requestNumber={dataRequest.dataRequestDoi ? parseInt(dataRequest.dataRequestDoi, 10) : 0}
                    title={displayTitle}
                    status={statusMessage}
                    myDataRequestsAdminHeader={false}
                />
            ) : (
                <>
                    {!isResearchTeamMember ? (
                        <AdminHeaderDetailTitleView
                            requestNumber={dataRequest.dataRequestDoi ? parseInt(dataRequest.dataRequestDoi, 10) : 0}
                            pi={pi}
                            status={statusMessage}
                            myDataRequestsAdminHeader={true}
                        />
                    ) : (
                        <AdminHeaderTitleView title={displayTitle} truncateText={true} />
                    )}
                </>
            )}
        </div>
    );
};
